import {Button} from '@bosch/react-frok';
import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import {
    ActionButton,
    AddBar,
    CloseButtonWrapper,
    ContentWrapper,
    FiltersContainer,
    FiltersContent,
    FilterSelectionWrapper,
    FilterSortBar,
    FilterSortContainer,
    FilterSortContainerBox,
    ResetChipsButtonContainer,
    SearchFieldWrapper,
    StyledChipClickableArea,
    TotalCountContainer
} from './SearchBar.style';
import {
    getFontSize,
    isNullOrEmpty,
    isPredefinedFilterApplicable,
    isSearchFilterApplied,
    validateFileTypes,
    validateObjectType,
    validateSearchTerm,
    validateSortOrder
} from '../../utils';
import {config} from '../../init/config';
import {SortOrder} from '../../models/SortOrder';
import {ObjectTypePicker} from '../ObjectTypePicker/ObjectTypePicker';
import {FileTypePickerAdvanced} from '../ObjectTypePicker/FileTypePickerAdvanced';
import {Valid} from '../validation/Valid';
import {getErrorDataValidationMessages, getSearchTermConstraintErrorMessage} from '../errors/ErrorRegister';
import {ErrorDialogue} from '../errors/ErrorDialogue';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {TemplatePropertySettings} from '../../models/TemplatePropertySettings';
import {isVisibleComponent} from '../../utils/TemplateUtils';
import {
    DOWNLOAD_CENTER_MAIN_ACTION,
    DOWNLOAD_CENTER_MOBILE_MAIN_ACTION,
    SEARCH_BAR,
    SEARCH_BAR_FILTER_CONTAINER,
    SEARCH_BAR_FILTER_CONTAINER_FAVORITES_FILTER,
    SEARCH_BAR_FILTER_CONTAINER_FILE_TYPE_FILTER,
    SEARCH_BAR_FILTER_CONTAINER_FILTER_SELECTION,
    SEARCH_BAR_FILTER_CONTAINER_FILTERS,
    SEARCH_BAR_FILTER_CONTAINER_OBJECT_TYPE_FILTER,
    SEARCH_BAR_FILTER_CONTAINER_PUBLIC_ACCESS_FILTER,
    SEARCH_BAR_FILTER_CONTAINER_RECENTLY_ADDED_FILTER,
    SEARCH_BAR_FILTER_CONTAINER_SORT_ORDER_FILTER,
    SEARCH_BAR_FILTER_CONTAINER_TOTAL_COUNT,
    SEARCH_BAR_MOBILE,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_FAVORITES_FILTER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILE_TYPE_FILTER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILTER_SELECTION,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILTERS,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_OBJECT_TYPE_FILTER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_PUBLIC_ACCESS_FILTER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_RECENTLY_ADDED_FILTER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_SORT_ORDER_FILTER,
    SEARCH_BAR_MOBILE_FILTER_CONTAINER_TOTAL_COUNT,
    SEARCH_BAR_MOBILE_SEARCH_TERM,
    SEARCH_BAR_SEARCH_TERM
} from '../../init/ui_component_constants';
import {objectTypeOptionResource} from '../ObjectTypePicker/resources/ObjectTypeResource';
import {
    getDropdownOptionPropsFromEntities,
    getFormattedDropDownOptionPropsEntity,
    TYPE_ALL
} from '../../utils/DropdownOptionPropsUtils';
import {SortType} from '../../models/SortType';

export interface SearchBarComponentProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly isMobileView: boolean;
    readonly isMobileResolution: boolean;
    readonly itemCount: number;
    readonly searchTerm: string | null;
    readonly recentlyAdded: number
    readonly recentlyAddedDefault: number;
    readonly isFavorite: boolean;
    readonly isPublic: boolean;
    readonly sortType: string;
    readonly sortTypeDefault: string;
    readonly sortOrder: string;
    readonly sortOderDefault: string;
    readonly objectType: string | null;
    readonly fileTypes: string[] | null;
    readonly publicOnlyAccess: boolean;
    onFilterDisable: () => void;
    onSortItemsChange: (sortOrder: string) => void;
    onSearchTermChange: (searchTerm: string | null) => void;
    onFileTypesChange: (fileTypes: string[] | null) => void;
    onRecentlyAddedChange: (noOfDays: number) => void;
    onFavoriteChange: (isFavorite: boolean) => void;
    onPublicChange: (isPublic: boolean) => void;
    onObjectTypeChange: (objectType: string) => void;
    mainAction: React.ReactNode;
}

/**
 * @name SearchBar
 * @description
 * The main page's search bar, corresponding and manipulating the view relative to any fetched items in the parent container
 */
export const SearchBar: React.VFC<SearchBarComponentProps> = (props) => {
    const [dialogError, setDialogError] = useState<Partial<{ headline?: string, errorDescription?: string }>>();
    const [searchTerm, setSearchTerm] = useState(props.searchTerm ? decodeURI(props.searchTerm).trim() : '');
    const [recentlyAdded, setRecentlyAdded] = useState<number>(props.recentlyAdded ?? 0);
    const [isFavorite, setIsFavorite] = useState<boolean>(props.isFavorite);
    const [isPublic, setIsPublic] = useState<boolean>(props.isPublic);
    const [objectType, setObjectType] = useState<string>(props.objectType ? props.objectType.toLowerCase() : '');
    const [fileTypes, setFileTypes] = useState<string[]>(props.fileTypes ? props.fileTypes : []);
    const [sortOrder, setSortOrder] = useState(props.sortOrder ? props.sortOrder.toLowerCase() : props.sortOderDefault);
    const [isValidSearchTerm, setIsValidSearchTerm] = useState<boolean>(!isNullOrEmpty(searchTerm));
    const [isValidSortOrder, setIsValidSortOrder] = useState<boolean>(validateSortOrder(props.sortType, props.sortTypeDefault, props.sortOrder, props.sortOderDefault));
    const [isValidObjectType, setIsValidObjectType] = useState<boolean>(validateObjectType(objectType));
    const [isValidFileTypes, setIsValidFileTypes] = useState<boolean>(validateFileTypes(fileTypes));
    const [isFiltersExpanded, setIsFiltersExpanded] = useState<boolean>(false);
    const isPredefinedFiltersApplicable = isPredefinedFilterApplicable(config.PREDEFINED_FILTERS);

    const filtersCountTotal = () => {
        let filterCount = fileTypes.length;
        if (isValidSearchTerm) {
            filterCount++;
        }
        if (recentlyAdded > 0) {
            filterCount++;
        }
        if (isFavorite) {
            filterCount++;
        }
        if (isPublic) {
            filterCount++;
        }
        if (isValidSortOrder) {
            filterCount++;
        }
        if (isValidObjectType) {
            filterCount++;
        }

        return filterCount;
    };

    useEffect(() => {
        if (!props.isFavorite) {
            handleDisableFavoriteFilter();
        }

        setIsFavorite(props.isFavorite);
    }, [props.isFavorite]);

    useEffect(() => {
        if (!props.isPublic) {
            handleDisablePublicFilter();
        }

        setIsPublic(props.isPublic)
    }, [props.isPublic])

    useEffect(() => {
        let validSearchTermState = !isNullOrEmpty(props.searchTerm);
        setIsValidSearchTerm(validSearchTermState);
        if (!validSearchTermState) {
            handleDisableSearchTermFilter();
            return;
        }

        setSearchTerm(props.searchTerm ? decodeURI(props.searchTerm).trim() : '');
    }, [props.searchTerm]);

    useEffect(() => {
        if (props.recentlyAdded <= 0) {
            handleDisableRecentlyAddedFilter();
            return;
        }

        setRecentlyAdded(props.recentlyAdded);
    }, [props.recentlyAdded]);

    useEffect(() => {
        let validObjectTypeState = validateObjectType(props.objectType);
        setIsValidObjectType(validObjectTypeState);
        if (!validObjectTypeState) {
            handleDisableObjectTypeFilter();
            return;
        }

        setObjectType(props.objectType ?? '');
    }, [props.objectType]);

    useEffect(() => {
        let validFileTypeStates = validateFileTypes(props.fileTypes);
        setIsValidFileTypes(validFileTypeStates);
        if (!validFileTypeStates) {
            handleDisableFileTypeFilter();
            return;
        }

        setFileTypes(props.fileTypes ?? []);
    }, [props.fileTypes]);

    useEffect(() => {
        setIsValidSortOrder(validateSortOrder(props.sortType, props.sortTypeDefault, props.sortOrder, props.sortOderDefault));
        if ((isNullOrEmpty(sortOrder)) && (!isNullOrEmpty(props.sortType))) {
            props.onSortItemsChange(props.sortOderDefault);
            setSortOrder(props.sortOderDefault);
            return;
        }

        setSortOrder(props.sortOrder);
    }, [props.sortType, props.sortTypeDefault, props.sortOrder, props.sortOderDefault]);

    useEffect(() => {
        setRecentlyAdded(props.recentlyAdded ?? 0);
    }, [props.recentlyAdded, props.recentlyAddedDefault]);

    const handleOnChangeSearchTerm = (searchTerm: string) => {
        let searchTermValue = decodeURI(searchTerm);
        setSearchTerm(searchTermValue);
        props.onSearchTermChange(searchTermValue.trim());
        setIsValidSearchTerm(!isNullOrEmpty(searchTermValue));
    };

    const handleSearchTermFilter = () => {
        if (isValidSearchTermInputValue) {
            props.onSearchTermChange(searchTerm.trim());
        } else {
            setDialogError(getErrorDataValidationMessages(t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT_HEADLINE'), getSearchTermConstraintErrorMessage()));
        }
    };

    const handleDisableSearchTermFilter = () => {
        setSearchTerm('');
        props.onSearchTermChange('');
    };

    const handleRecentlyAddedFilter = () => {
        let recentlyAddedState = (recentlyAdded > 0) ? 0 : props.recentlyAddedDefault;
        props.onRecentlyAddedChange(recentlyAddedState);
        setRecentlyAdded(recentlyAddedState);
    };

    const handleDisableRecentlyAddedFilter = () => {
        props.onRecentlyAddedChange(0);
        setRecentlyAdded(0);
    };

    const handleFavoriteFilter = () => {
        props.onFavoriteChange(!isFavorite);
        setIsFavorite(!isFavorite);
    };

    const handleDisableFavoriteFilter = () => {
        props.onFavoriteChange(false);
        setIsFavorite(false);
    };

    const handleSortOrderFilter = () => {
        let sortOrderStatus = (SortOrder.DESC === sortOrder) ? SortOrder.ASC : SortOrder.DESC;
        setIsValidSortOrder(validateSortOrder(props.sortType, props.sortTypeDefault, sortOrderStatus, props.sortOderDefault));
        props.onSortItemsChange(sortOrderStatus);
        setSortOrder(sortOrderStatus);
    };

    const handleDisableSortOrderFilter = () => {
        setIsValidSortOrder(validateSortOrder(props.sortType, props.sortTypeDefault, props.sortOderDefault, props.sortOderDefault));
        props.onSortItemsChange(props.sortOderDefault);
        setSortOrder(props.sortOderDefault);
    };

    const handlePublicFilter = () => {
        props.onPublicChange(!isPublic);
        setIsPublic(!isPublic);
    };

    const handleDisablePublicFilter = () => {
        props.onPublicChange(false);
        setIsPublic(false);
    };

    const handleObjectTypeFilter = (objectTypeSelection: string) => {
        props.onObjectTypeChange(objectTypeSelection);
        setObjectType(objectTypeSelection);
    };

    const handleDisableObjectTypeFilter = () => {
        props.onObjectTypeChange('');
        setObjectType('');
    };

    const handleFileTypeFilter = (fileTypeSelection: string[]) => {
        if (fileTypeSelection && (fileTypeSelection.length > 0)) {
            props.onFileTypesChange(fileTypeSelection);
            setFileTypes(fileTypeSelection);
            return;
        }

        handleDisableFileTypeFilter();
    };

    const handleDisableFileTypeFilter = () => {
        props.onFileTypesChange([]);
        setFileTypes([]);
    };

    const handleDisableSingleFileTypeFilter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, fileTypeSelection: string) => {
        if (isNullOrEmpty(fileTypeSelection)) {
            handleDisableFileTypeFilter();
            return;
        }

        let fileTypeState = fileTypes.filter(type => (fileTypeSelection !== type));
        if ((!fileTypeState) || (fileTypeState.length === 0)) {
            handleDisableFileTypeFilter();
            return;
        }

        props.onFileTypesChange(fileTypeState);
        setFileTypes(fileTypeState);
    };

    const handleResetAllFilters = () => {
        setIsFiltersExpanded(false);
        handleDisableSearchTermFilter();
        handleDisableRecentlyAddedFilter();
        handleDisableFavoriteFilter();
        handleDisablePublicFilter();
        handleDisableObjectTypeFilter();
        handleDisableFileTypeFilter();
        handleDisableSortOrderFilter();
        props.onFilterDisable();
    };

    const handleApplyPredefinedFilters = () => {
        let predefinedFilters = config.PREDEFINED_FILTERS;
        if ((predefinedFilters === undefined) || (!predefinedFilters)) {
            return;
        }

        handleResetAllFilters();
        (predefinedFilters.searchParam && (!isNullOrEmpty(predefinedFilters.searchParam))) && handleOnChangeSearchTerm(predefinedFilters.searchParam);
        (predefinedFilters.objectType && (TYPE_ALL !== predefinedFilters.objectType.toLowerCase())) && handleObjectTypeFilter(predefinedFilters.objectType);
        (predefinedFilters.fileTypes && (predefinedFilters.fileTypes.length > 0)) && handleFileTypeFilter(predefinedFilters.fileTypes);
    };

    const populateFilters = () => {
        if (!(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_FILTERS, props.templatePropertySettings)
            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILTERS, props.templatePropertySettings))))) {
            return;
        }

        let fontSize = getFontSize([
            t('FILTERS_AREA_CLOSE_FILTERS_LABEL')
        ], 10);

        return (
            <ContentWrapper id={generateIdentifier()}>
                {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_OBJECT_TYPE_FILTER, props.templatePropertySettings)
                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_OBJECT_TYPE_FILTER, props.templatePropertySettings)))) &&
                    <ObjectTypePicker
                        dataSource={getDropdownOptionPropsFromEntities(objectTypeOptionResource)}
                        defaultValue={objectType ?? ''}
                        resetState={!isValidObjectType}
                        resetInput={false}
                        disabled={false}
                        onChange={handleObjectTypeFilter}/>
                }
                {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_FILE_TYPE_FILTER, props.templatePropertySettings)
                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILE_TYPE_FILTER, props.templatePropertySettings)))) &&
                    <FileTypePickerAdvanced isMobileResolution={props.isMobileResolution}
                                            resetState={!isValidFileTypes}
                                            resetInput={false}
                                            fileTypes={fileTypes}
                                            onChange={handleFileTypeFilter}/>
                }
                <FiltersContent id={generateIdentifier()} isMobileResolution={props.isMobileResolution}>
                    <CloseButtonWrapper id={generateIdentifier()}>
                        <ActionButton id={generateIdentifier()}
                                      icon='up'
                                      mode='secondary'
                                      label={t('FILTERS_AREA_CLOSE_FILTERS_LABEL')}
                                      title={t('FILTERS_AREA_CLOSE_FILTERS_LABEL')}
                                      fontSize={fontSize}
                                      onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}/>
                    </CloseButtonWrapper>
                </FiltersContent>
            </ContentWrapper>
        );
    };

    let isValidSearchTermInputValue = validateSearchTerm(searchTerm);
    let isVisibleResetButton = isSearchFilterApplied(searchTerm, recentlyAdded, isFavorite, isPublic, isValidSortOrder, objectType, fileTypes);

    const renderSelection = () => {
        if (!(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_FILTER_SELECTION, props.templatePropertySettings)
            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILTER_SELECTION, props.templatePropertySettings))))) {
            return;
        }

        return (
            <SearchFieldWrapper id={generateIdentifier()}>
                {isValidSearchTerm &&
                    <StyledChipClickableArea id={generateIdentifier()}
                                             buttonClose label={searchTerm}
                                             onClick={handleDisableSearchTermFilter}/>}
                {(recentlyAdded > 0) &&
                    <StyledChipClickableArea id={generateIdentifier()}
                                             buttonClose
                                             label={t('SEARCH_BAR_COMPONENT_RECENTLY_ADDED', {days: recentlyAdded})}
                                             onClick={handleDisableRecentlyAddedFilter}/>}
                {isFavorite &&
                    <StyledChipClickableArea id={generateIdentifier()}
                                             buttonClose label={t('SEARCH_BAR_COMPONENT_FAVORITES')}
                                             onClick={handleDisableFavoriteFilter}/>}
                {isPublic &&
                    <StyledChipClickableArea id={generateIdentifier()}
                                             buttonClose label={t('SEARCH_BAR_COMPONENT_PUBLIC_ACCESS')}
                                             onClick={handleDisablePublicFilter}/>}
                {isValidSortOrder &&
                    <StyledChipClickableArea
                        id={generateIdentifier()}
                        buttonClose
                        label={t('SEARCH_BAR_COMPONENT_SORT_ORDER', {order: (SortOrder.DESC === sortOrder) ? t('SORT_ORDER_DESC') : t('SORT_ORDER_ASC')})}
                        onClick={handleSortOrderFilter}
                    />
                }
                {(isValidObjectType && objectType) &&
                    <StyledChipClickableArea id={generateIdentifier()}
                                             buttonClose
                                             label={t('SEARCH_BAR_COMPONENT_OBJECT_TYPE', {type: t(getFormattedDropDownOptionPropsEntity(objectType))})}
                                             onClick={handleDisableObjectTypeFilter}/>}
                {isValidFileTypes && fileTypes?.map(type =>
                    <StyledChipClickableArea id={generateIdentifier()}
                                             key={type}
                                             buttonClose
                                             label={type}
                                             onClick={(e) => handleDisableSingleFileTypeFilter(e, type)}/>
                )}
                <ResetChipsButtonContainer id={generateIdentifier()}>
                    {isVisibleResetButton &&
                        <Button id={generateIdentifier()}
                                icon='reset'
                                label={t('SEARCH_BAR_COMPONENT_RESET_ALL_LABEL')}
                                title={t('SEARCH_BAR_COMPONENT_RESET_ALL_LABEL')}
                                mode='tertiary'
                                onClick={handleResetAllFilters}/>
                    }
                    {isPredefinedFiltersApplicable &&
                        <Button id={generateIdentifier()}
                                icon='refresh'
                                label={t('FILTERS_AREA_APPLY_DEFAULT_LABEL')}
                                title={t('FILTERS_AREA_APPLY_DEFAULT_LABEL')}
                                mode='tertiary'
                                onClick={handleApplyPredefinedFilters}/>
                    }
                </ResetChipsButtonContainer>
            </SearchFieldWrapper>
        );
    };

    const renderErrorDialog = (populate: boolean, headline: string | undefined, description: string | undefined) => {
        return <ErrorDialogue populate={populate}
                              headline={headline}
                              description={description}
                              onChange={() => setDialogError(undefined)}/>
    };

    if (!(isVisibleComponent(SEARCH_BAR, props.templatePropertySettings)
        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE, props.templatePropertySettings))))) {
        return null;
    }

    let recentlyAddedState = (recentlyAdded > 0) ? recentlyAdded : props.recentlyAddedDefault

    return (
        <SearchFieldWrapper id={generateIdentifier()}>
            <SearchFieldWrapper id={generateIdentifier()}>
                {(isVisibleComponent(SEARCH_BAR_SEARCH_TERM, props.templatePropertySettings)
                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_SEARCH_TERM, props.templatePropertySettings)))) &&
                    <Valid isValid={isValidSearchTermInputValue}
                           message={t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT', {param1: 2})}>
                        <SearchFieldWrapper id={generateIdentifier()}
                                            key='searchField'
                                            className='a-text-field a-text-field--search'>
                            <input id={generateIdentifier()}
                                   className='searchInputField'
                                   type='search'
                                   placeholder={t('SEARCH_BAR_COMPONENT_PLACEHOLDER_SEARCH_TERM')}
                                   value={decodeURI(searchTerm)}
                                   onChange={(event) => handleOnChangeSearchTerm(event.target.value)}
                                   onKeyDown={(event) => {
                                       if (event.key === 'Enter') {
                                           handleSearchTermFilter();
                                       }
                                   }}/>
                            <Button id={generateIdentifier()}
                                    type='submit'
                                    className='a-text-field__icon-search'
                                    title={t('GENERAL_SUBMIT_LABEL')}
                                    onClick={handleSearchTermFilter}>
                                <i id={generateIdentifier()} className='a-icon ui-ic-search'
                                   title={t('SEARCH_BAR_COMPONENT_TITLE')}/>
                            </Button>
                        </SearchFieldWrapper>
                    </Valid>
                }
                {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER, props.templatePropertySettings)
                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER, props.templatePropertySettings)))) &&
                    <FilterSortContainerBox id={generateIdentifier()}>
                        <FilterSortBar id={generateIdentifier()}>
                            {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_TOTAL_COUNT, props.templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_TOTAL_COUNT, props.templatePropertySettings)))) &&
                                <TotalCountContainer id={generateIdentifier()} key='totalContainer'
                                                     className='a-text'>
                                    <h2 id={generateIdentifier()} key='totalItems' className='-size-l'>
                                        {props.itemCount} {t('SEARCH_BAR_COMPONENT_SEARCH_ITEM_COUNT')}
                                    </h2>
                                </TotalCountContainer>
                            }
                            <FilterSortContainer id={generateIdentifier()}
                                                 key='buttonContainer'
                                                 className='a-link a-link--simple'>
                                {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_FILTERS, props.templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILTERS, props.templatePropertySettings)))) &&
                                    <Button id={generateIdentifier()}
                                            className='filters-toggle'
                                            mode='tertiary'
                                            icon='filter'
                                            label={props.isMobileResolution ? `(${filtersCountTotal()})` : t('SEARCH_BAR_COMPONENT_FILTER_LABEL', {count: filtersCountTotal()})}
                                            title={t('SEARCH_BAR_COMPONENT_FILTER_TOOLTIP_LABEL')}
                                            onClick={() => {
                                                setIsFiltersExpanded(!isFiltersExpanded);
                                            }}/>
                                }
                                {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_RECENTLY_ADDED_FILTER, props.templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_RECENTLY_ADDED_FILTER, props.templatePropertySettings)))) &&
                                    <Button id={generateIdentifier()}
                                            mode='tertiary'
                                            icon={(recentlyAdded > 0) ? 'clock-stop' : 'clock'}
                                            label={props.isMobileResolution ? `(${recentlyAddedState})` : t('SEARCH_BAR_COMPONENT_RECENTLY_ADDED', {days: recentlyAddedState})}
                                            title={t('SEARCH_BAR_COMPONENT_RECENTLY_ADDED', {days: recentlyAddedState})}
                                            onClick={handleRecentlyAddedFilter}/>
                                }
                                {((!props.publicOnlyAccess) && (isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_FAVORITES_FILTER, props.templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_FAVORITES_FILTER, props.templatePropertySettings))))) &&
                                    <Button id={generateIdentifier()}
                                            mode='tertiary'
                                            icon={(isFavorite) ? 'wishlist-filled' : 'wishlist'}
                                            label={props.isMobileResolution ? '' : t('SEARCH_BAR_COMPONENT_FAVORITES')}
                                            title={t('SEARCH_BAR_COMPONENT_FAVORITES')}
                                            onClick={handleFavoriteFilter}/>}
                                {((!props.publicOnlyAccess) && (isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_PUBLIC_ACCESS_FILTER, props.templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_PUBLIC_ACCESS_FILTER, props.templatePropertySettings))))) &&
                                    <Button id={generateIdentifier()}
                                            mode='tertiary'
                                            icon={(isPublic) ? 'lock-closed-checkmark' : 'lock-closed'}
                                            label={props.isMobileResolution ? '' : t('SEARCH_BAR_COMPONENT_PUBLIC_ACCESS')}
                                            title={t('SEARCH_BAR_COMPONENT_PUBLIC_ACCESS')}
                                            onClick={handlePublicFilter}/>}
                                {(isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_SORT_ORDER_FILTER, props.templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_SORT_ORDER_FILTER, props.templatePropertySettings)))) &&
                                    <Button id={generateIdentifier()}
                                            mode='tertiary'
                                            icon={((SortType.UD === props.sortType) ? 'up-down' : 'sort-alphabetically')}
                                            title={(SortType.UD === props.sortType) ? t('SEARCH_BAR_COMPONENT_SORT_BY_DATE') : t('SEARCH_BAR_COMPONENT_SORT_ALPHABETICALLY')}
                                            onClick={handleSortOrderFilter}/>
                                }
                            </FilterSortContainer>
                        </FilterSortBar>
                        {(isVisibleComponent(DOWNLOAD_CENTER_MAIN_ACTION, props.templatePropertySettings)
                                && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(DOWNLOAD_CENTER_MOBILE_MAIN_ACTION, props.templatePropertySettings)))) &&
                            <AddBar id={generateIdentifier()}>
                                {props.mainAction}
                            </AddBar>
                        }
                    </FilterSortContainerBox>
                }
            </SearchFieldWrapper>
            {(isVisibleResetButton || isFiltersExpanded) &&
                <SearchFieldWrapper id={generateIdentifier()}>
                    <FiltersContainer id={generateIdentifier()} className='-secondary' expanded={isFiltersExpanded}>
                        {populateFilters()}
                    </FiltersContainer>
                    {(isVisibleResetButton && (isVisibleComponent(SEARCH_BAR_FILTER_CONTAINER_FILTER_SELECTION, props.templatePropertySettings)
                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(SEARCH_BAR_MOBILE_FILTER_CONTAINER_FILTER_SELECTION, props.templatePropertySettings))))) &&
                        <FilterSelectionWrapper id={generateIdentifier()}>
                            {renderSelection()}
                        </FilterSelectionWrapper>
                    }
                </SearchFieldWrapper>
            }
            {renderErrorDialog(!!dialogError, dialogError?.headline, dialogError?.errorDescription)}
        </SearchFieldWrapper>
    );
};
